import React, {FC} from 'react';

import {useAppDispatch, useAppSelector} from '../../helpers/reduxHelpers';
import {InboxModel} from '../../models/api/inboxModel';
import {PermissionsEnum} from '../../models/api/roleModel';
import {
  SettingsInboxesSubPagesEnum,
  SettingsInboxesUrl,
  SettingsResourcePagesEnum,
} from '../../models/url/settingsUrlModel';
import {getHasPermission} from '../../store/data/access/accessSelectors';
import {updateInboxAsyncAction} from '../../store/data/inboxes/inboxesActions';
import {SettingsPanelId} from '../../store/panels/panelStateBase';
import {useFetchInbox, useInvalidateInbox} from '../fetchers/inboxes';
import {SettingsSingleChannelContainer} from './channels/settingsSingleChannelContainer';
import {SettingsInboxesBusinessHours} from './settingsInboxesBusinessHours';
import {SettingsInboxesChannels} from './settingsInboxesChannels';
import {SettingsInboxesImportCustomAttributes} from './settingsInboxesImportCustomAttributes';
import {SettingsInboxesList} from './settingsInboxesList';
import {SettingsInboxOverview} from './settingsInboxesOverview';
import {SettingsInboxesPreferences} from './settingsInboxesPreferences';

/*
 * Props.
 */

interface SettingsInboxesProps {
  panelId: SettingsPanelId;
  url: SettingsInboxesUrl;
}

/*
 * Component.
 */

export const SettingsInboxes: FC<SettingsInboxesProps> = (props) => {
  const {panelId, url} = props;

  const hasPermission = useAppSelector(getHasPermission);
  const dispatch = useAppDispatch();
  const {inbox: fetchedInbox} = useFetchInbox(props.url.inboxId);

  const canImportCustomFieldAttributesCsv = hasPermission(url.namespace, PermissionsEnum.INBOX_UPDATE);

  const invalidateInbox = useInvalidateInbox();

  const onInboxUpdate = async (newInbox: InboxModel) => {
    await dispatch(updateInboxAsyncAction(newInbox));
    invalidateInbox(newInbox.id, {shouldUncache: true});
  };

  if (fetchedInbox) {
    if (
      url.resourcePage === SettingsResourcePagesEnum.EDIT &&
      url.subPage === SettingsInboxesSubPagesEnum.OVERVIEW
    ) {
      return <SettingsInboxOverview inbox={fetchedInbox} url={url} onInboxUpdate={onInboxUpdate} />;
    }

    if (
      url.resourcePage === SettingsResourcePagesEnum.EDIT &&
      url.subPage === SettingsInboxesSubPagesEnum.SETTINGS
    ) {
      return <SettingsInboxesPreferences inbox={fetchedInbox} url={url} onInboxUpdate={onInboxUpdate} />;
    }

    if (
      url.resourcePage === SettingsResourcePagesEnum.EDIT &&
      url.subPage === SettingsInboxesSubPagesEnum.BUSINESS_HOURS
    ) {
      return (
        <SettingsInboxesBusinessHours
          inbox={fetchedInbox}
          url={url}
          onInboxUpdate={onInboxUpdate}
          panelId={panelId}
        />
      );
    }

    if (
      url.resourcePage === SettingsResourcePagesEnum.EDIT &&
      url.subPage === SettingsInboxesSubPagesEnum.CHANNELS
    ) {
      if (!url.channelId) {
        return <SettingsInboxesChannels inbox={fetchedInbox} url={url} />;
      }

      return (
        <SettingsSingleChannelContainer
          panelId={props.panelId}
          inbox={fetchedInbox}
          url={url}
          channelId={url.channelId}
        />
      );
    }
  }

  if (url.resourcePage === SettingsResourcePagesEnum.OVERVIEW) {
    return <SettingsInboxesList url={url} panelId={panelId} />;
  }

  if (
    canImportCustomFieldAttributesCsv &&
    url.resourcePage === SettingsResourcePagesEnum.IMPORT_CUSTOM_ATTRIBUTES
  ) {
    return <SettingsInboxesImportCustomAttributes url={url} />;
  }

  return null;
};
